import { useCallback, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, InlineEdit, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { formatDate, formatNumber, humanise, issueBadge, rockBadge } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Picture, Vto } from '../lib/types';
import { CultureBuilder, type CulturePrinciple } from './vto/CultureBuilder';

/**
 * The EOS Vision/Traction Organizer.
 *
 * Edits save on blur rather than behind a Save button — this is a document
 * people work through in a meeting, and a modal per field would stop the
 * conversation dead.
 */
export function VtoPage() {
  const vto = useResource<Vto>('/vto/current');
  const [error, setError] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  const vtoId = vto.data?.VtoId;
  const reload = vto.reload;

  /**
   * Write the Culture Builder's principles in as core values.
   *
   * APPENDS. Nothing already in the list is touched, so running the builder
   * again after a first pass adds to it rather than quietly discarding
   * somebody's considered wording — deleting is one click per row and is the
   * reader's call, not this function's.
   *
   * One at a time and IN ORDER, not in parallel: SortOrder is what the list is
   * read in, and firing five POSTs at once puts them in whatever order the
   * database happens to finish them.
   *
   * The value words that produced each principle are appended to its sentence.
   * They are the evidence for it — "Accountability" alone is a poster, and
   * "Accountability — ownership, follow-through, candour" is something you can
   * hold a review against.
   */
  const adoptPrinciples = useCallback(
    async (principles: CulturePrinciple[]) => {
      /* Thrown, not returned: a quiet return resolves, and the modal closes as
         though the values had been saved. */
      if (!vtoId) throw new Error('The V/TO has not loaded yet — try again in a moment.');
      /* Past the end of what is already there, so an append does not interleave
         with it. */
      const start = (vto.data?.coreValues ?? []).reduce(
        (max, cv) => Math.max(max, cv.SortOrder ?? 0),
        -10,
      );
      /* A failure part way leaves the earlier principles saved, so the list is
         reloaded either way and the error says how many landed — running the
         builder again appends the whole set, and whoever does that should know
         the first few are already there. */
      let saved = 0;
      try {
        for (const [i, p] of principles.entries()) {
          const words = (p.values ?? []).filter(Boolean);
          const detail = [p.description?.trim(), words.length ? words.join(', ') : null]
            .filter(Boolean)
            .join(' — ');
          await api.post(`/vto/${vtoId}/core-values`, {
            value: p.name.trim().slice(0, 200),
            description: detail.slice(0, 1000) || null,
            sortOrder: start + (i + 1) * 10,
          });
          saved += 1;
        }
      } catch (err) {
        const why = err instanceof Error ? err.message : 'Could not save those values';
        throw new Error(
          saved === 0
            ? why
            : `${why}. ${saved} of ${principles.length} were added before it stopped — they are in the list now.`,
        );
      } finally {
        reload();
      }
    },
    [vtoId, reload, vto.data?.coreValues],
  );

  if (vto.loading) return <Loading label="Loading the V/TO" />;
  if (vto.error) return <ErrorBanner message={vto.error} />;
  if (!vto.data) return <Empty title="No V/TO found" />;

  const v = vto.data;

  const patch = (body: Record<string, unknown>) =>
    api.put(`/vto/${v.VtoId}`, body).then(vto.reload).catch(handle);

  const handle = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save');

  /* Open only. A resolved issue is a thing that happened, and the V/TO is
     about what is in the way now — EOS keeps the history. */
  const openIssues = v.issues.filter((i) => i.Status === 'open' || i.Status === 'in_progress');

  const threeYear = v.pictures.find((p) => p.Horizon === 'three_year');
  const oneYear = v.pictures.find((p) => p.Horizon === 'one_year');

  return (
    <>
      <PageHead
        title="V/TO"
        lede="The Vision/Traction Organizer. Vision on the left of the page in EOS; here it runs top to bottom — the eight-question vision first, then the plan that delivers it. Rocks and issues are worked in EOS, in the meeting that owns them."
      />

      {error && <ErrorBanner message={error} />}

      <VtoContents />

      {/* The stat band went with the lists. Three of its four tiles counted
          rocks and issues, and the fourth counted core values — which the Core
          Values section says better, in words, two inches further down. A
          document does not need a dashboard on top of it. */}

      <div className="stack">
        <VtoBand
          part="Vision"
          title="Where the business is going"
          lede="The eight questions every person in the company should be able to answer the same way."
        />

        {/* --- Core Values ------------------------------------------------ */}
        <section className="card" id="vto-values">
          <div className="card-head">
            <div>
              <span className="eyebrow">Vision</span>
              <h2>Core Values</h2>
              <p className="sub">The handful of behaviours you hire, fire and review against.</p>
            </div>
            <div className="row">
              {/* ALWAYS OFFERED. This was hidden once the list had anything in
                  it, on the reasoning that the builder would then have to
                  choose between replacing and appending. The cost of that was
                  worse than the ambiguity: one click of Add value leaves a row
                  reading "New core value", and the builder — the thing that
                  helps you decide what the values should BE — vanished at
                  exactly the moment somebody went looking for it. It appends,
                  which is never destructive, and the modal says so. */}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setBuilderOpen(true)}
              >
                Define with the Culture Builder
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  void api
                    .post(`/vto/${v.VtoId}/core-values`, {
                      value: 'New core value',
                      sortOrder: v.coreValues.length * 10,
                    })
                    .then(vto.reload)
                    .catch(handle)
                }
              >
                Add value
              </button>
            </div>
          </div>
          <div className="card-body stack">
            {v.coreValues.length === 0 && (
              <p className="muted small">
                No core values yet. Work them out in the Culture Builder, or add them by hand.
              </p>
            )}
            {v.coreValues.map((cv) => (
              <div key={cv.CoreValueId} className="row-between" style={{ alignItems: 'flex-start' }}>
                <div className="grow">
                  <InlineEdit
                    value={cv.Value}
                    rows={1}
                    placeholder="Core value"
                    className="inline-edit strong"
                    onSave={(next) =>
                      void api.put(`/vto/core-values/${cv.CoreValueId}`, { value: next }).then(vto.reload).catch(handle)
                    }
                  />
                  <InlineEdit
                    value={cv.Description}
                    rows={1}
                    placeholder="What it looks like in practice…"
                    className="inline-edit small muted"
                    onSave={(next) =>
                      void api
                        .put(`/vto/core-values/${cv.CoreValueId}`, { description: next })
                        .then(vto.reload)
                        .catch(handle)
                    }
                  />
                </div>
                <DeleteButton
                  what={`the value "${cv.Value}"`}
                  label="Remove"
                  onConfirm={() =>
                    void api.del(`/vto/core-values/${cv.CoreValueId}`).then(vto.reload).catch(handle)
                  }
                />
              </div>
            ))}
          </div>
        </section>

        <CultureBuilder
          open={builderOpen}
          existingCount={v.coreValues.length}
          onClose={() => setBuilderOpen(false)}
          onAdopt={adoptPrinciples}
        />

        {/* --- Core Focus + 10-Year Target -------------------------------- */}
        <div className="grid grid-2">
          <section className="card" id="vto-focus">
            <div className="card-head">
              <div>
                <span className="eyebrow">Vision</span>
                <h2>Core Focus</h2>
                <p className="sub">Why you exist, and the space you play in.</p>
              </div>
            </div>
            <div className="card-body stack">
              <div className="field">
                <label>Purpose / Cause / Passion</label>
                <InlineEdit
                  value={v.Purpose}
                  rows={3}
                  placeholder="Why does this business exist?"
                  onSave={(purpose) => void patch({ purpose })}
                />
              </div>
              <div className="field">
                <label>Our Niche</label>
                <InlineEdit
                  value={v.Niche}
                  rows={3}
                  placeholder="What do we do better than anyone?"
                  onSave={(niche) => void patch({ niche })}
                />
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <div>
                <span className="eyebrow">Vision</span>
                <h2 id="vto-target">10-Year Target</h2>
                <p className="sub">The one big number or milestone everything points at.</p>
              </div>
            </div>
            <div className="card-body stack">
              <div className="field">
                <label>Target</label>
                <InlineEdit
                  value={v.TenYearTarget}
                  rows={3}
                  placeholder="Where will this business be in ten years?"
                  onSave={(tenYearTarget) => void patch({ tenYearTarget })}
                />
              </div>
              <div className="field">
                <label>By when</label>
                <input
                  className="input"
                  type="date"
                  defaultValue={v.TenYearDate?.slice(0, 10) ?? ''}
                  onBlur={(e) => void patch({ tenYearDate: e.target.value || null })}
                />
              </div>
            </div>
          </section>
        </div>

        {/* --- Marketing Strategy ----------------------------------------- */}
        <section className="card" id="vto-marketing">
          <div className="card-head">
            <div>
              <span className="eyebrow">Vision</span>
              <h2>Marketing Strategy</h2>
              <p className="sub">Who you sell to, what makes you different, and what you promise.</p>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                void api
                  .post(`/vto/${v.VtoId}/uniques`, { text: 'New unique', sortOrder: v.uniques.length * 10 })
                  .then(vto.reload)
                  .catch(handle)
              }
            >
              Add unique
            </button>
          </div>
          <div className="card-body stack">
            <div className="field">
              <label>Target market — “The List”</label>
              <InlineEdit
                value={v.TargetMarket}
                rows={2}
                placeholder="Demographic, geographic and psychographic profile of your ideal client"
                onSave={(targetMarket) => void patch({ targetMarket })}
              />
            </div>

            <div className="field">
              <label>Three uniques</label>
              {v.uniques.length === 0 && <p className="muted small">None yet.</p>}
              {v.uniques.map((u, i) => (
                <div key={u.UniqueId} className="row" style={{ alignItems: 'flex-start' }}>
                  <span className="mono muted" style={{ paddingTop: 6 }}>
                    {i + 1}.
                  </span>
                  <div className="grow">
                    <InlineEdit
                      value={u.Text}
                      rows={1}
                      placeholder="What makes you different"
                      onSave={(text) =>
                        void api.put(`/vto/uniques/${u.UniqueId}`, { text }).then(vto.reload).catch(handle)
                      }
                    />
                  </div>
                  <DeleteButton
                    what="this unique"
                    label="Remove"
                    onConfirm={() => void api.del(`/vto/uniques/${u.UniqueId}`).then(vto.reload).catch(handle)}
                  />
                </div>
              ))}
            </div>

            <div className="form-grid">
              <div className="field">
                <label>Proven process</label>
                <InlineEdit
                  value={v.ProvenProcess}
                  rows={2}
                  placeholder="The named process you walk every client through"
                  onSave={(provenProcess) => void patch({ provenProcess })}
                />
              </div>
              <div className="field">
                <label>Guarantee</label>
                <InlineEdit
                  value={v.Guarantee}
                  rows={2}
                  placeholder="The promise that removes the risk of buying"
                  onSave={(guarantee) => void patch({ guarantee })}
                />
              </div>
            </div>
          </div>
        </section>

        {/* --- Pictures ---------------------------------------------------- */}
        <div className="grid grid-2">
          {threeYear && (
            <PictureCard
              anchor="vto-3year"
              picture={threeYear}
              title="3-Year Picture"
              eyebrow="Vision"
              lede="What the business looks and feels like three years out."
              onChange={vto.reload}
              onError={handle}
            />
          )}
          {oneYear && (
            <PictureCard
              anchor="vto-1year"
              picture={oneYear}
              title="1-Year Plan"
              eyebrow="Traction"
              lede="The goals that must land this year to stay on the three-year path."
              onChange={vto.reload}
              onError={handle}
            />
          )}
        </div>

        <VtoBand
          part="Traction"
          title="How it actually gets there"
          lede="Ninety days at a time, and everything currently in the way."
        />

        {/* Back on the page, but as a SUMMARY rather than the two full
            tables that were here before.

            Those tables were most of the document — 26,500 pixels, the issues
            one alone three quarters of it — because each row carried an owner
            picker, a status select and a delete button. Editing lives in EOS,
            in the meeting that owns the item; what the V/TO needs is the
            shape: how much is in flight, and whose. Read-only rows in columns
            do that in a fraction of the height.

            Grouped by BUSINESS UNIT, resolved the same way the business review
            resolves it — tag, then meeting, then owner. Both pages read
            lib/unitAttribution.ts so the two cannot disagree about whose a
            rock is. */}
        <TractionSummary
          id="vto-rocks"
          title="Quarterly Rocks"
          lede="The 90-day priorities, by the unit that owns them. Set and worked in EOS."
          units={v.units}
          items={v.rocks.map((r) => ({
            key: r.RockId,
            title: r.Title,
            owner: r.OwnerName,
            unitId: r.ResolvedUnitId,
            badge: rockBadge(r.Status),
            note: humanise(r.Status),
          }))}
          empty="No rocks set."
        />

        <TractionSummary
          id="vto-issues"
          title="Issues List"
          lede="Everything currently in the way, by unit. Solved in EOS, in the meeting that raised it."
          units={v.units}
          items={openIssues.map((i) => ({
            key: i.IssueId,
            title: i.Title,
            owner: i.OwnerName,
            unitId: i.ResolvedUnitId,
            badge: issueBadge(i.Priority),
            note: `${i.Priority} · ${i.Term === 'long' ? 'long term' : 'short term'}`,
          }))}
          empty="No open issues."
        />
      </div>
    </>
  );
}

/** One row of a traction summary, flattened out of a rock or an issue. */
interface SummaryItem {
  key: number;
  title: string;
  owner: string | null;
  unitId: number | null;
  /** A badge-* class from lib/format, so a rock and an issue read alike. */
  badge: string;
  note: string;
}

/**
 * How many rows a unit shows before it offers the rest.
 *
 * Per UNIT rather than per section, which is the difference between a page and
 * a wall: Executive holds 105 of the 161 open issues, so one shared cap would
 * either truncate the small units to nothing or let Executive run for two
 * screens on its own. Five fills a column without setting the pace for the
 * page, and the count in the heading says what is behind it.
 */
const PER_UNIT = 5;

/**
 * Rocks or issues, small, in columns, grouped by business unit.
 *
 * READ-ONLY, deliberately. The tables this replaces carried an owner picker, a
 * status select and a delete button on every row, which is what made them
 * 26,500 pixels of document. Items are set and worked in EOS, in the meeting
 * that owns them; here they are the shape of what is in flight.
 *
 * CSS COLUMNS rather than a grid. Unit blocks are wildly uneven — Executive
 * against NOC's single row — and a grid would leave a row of tall gaps beside
 * every short block. Columns let a short unit sit straight under a short one.
 */
function TractionSummary({
  id,
  title,
  lede,
  units,
  items,
  empty,
}: {
  id: string;
  title: string;
  lede: string;
  units: { BusinessUnitId: number; Name: string }[];
  items: SummaryItem[];
  empty: string;
}) {
  const [expanded, setExpanded] = useState<number[]>([]);

  /* Units with nothing in them are dropped here rather than in the query. The
     endpoint sends every active unit so this can say "no rocks at all" apart
     from "no rocks in Sales", and an empty column is noise on a page whose
     whole problem was height. */
  const groups = units
    .map((u) => ({ ...u, items: items.filter((i) => i.unitId === u.BusinessUnitId) }))
    .filter((g) => g.items.length > 0);

  /* Anything that resolved to nothing, or to a unit no longer active. Last,
     and named for what it is: leaving it out would make the counts lie. */
  const known = new Set(units.map((u) => u.BusinessUnitId));
  const orphans = items.filter((i) => i.unitId === null || !known.has(i.unitId));

  return (
    <section className="card" id={id}>
      <div className="card-head">
        <div>
          <span className="eyebrow">Traction</span>
          <h2>
            {title} <span className="vto-sum-total">{items.length}</span>
          </h2>
          <p className="sub">{lede}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card-body">
          <p className="muted small">{empty}</p>
        </div>
      ) : (
        <div className="card-body">
          <div className="vto-sum-cols">
            {groups.map((g) => (
              <UnitColumn
                key={g.BusinessUnitId}
                name={g.Name}
                items={g.items}
                open={expanded.includes(g.BusinessUnitId)}
                onToggle={() =>
                  setExpanded((prev) =>
                    prev.includes(g.BusinessUnitId)
                      ? prev.filter((x) => x !== g.BusinessUnitId)
                      : [...prev, g.BusinessUnitId],
                  )
                }
              />
            ))}
            {orphans.length > 0 && (
              <UnitColumn
                name="No unit"
                items={orphans}
                open={expanded.includes(-1)}
                onToggle={() =>
                  setExpanded((prev) =>
                    prev.includes(-1) ? prev.filter((x) => x !== -1) : [...prev, -1],
                  )
                }
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function UnitColumn({
  name,
  items,
  open,
  onToggle,
}: {
  name: string;
  items: SummaryItem[];
  open: boolean;
  onToggle: () => void;
}) {
  const shown = open ? items : items.slice(0, PER_UNIT);
  return (
    <div className="vto-sum-unit">
      <h3>
        {name} <span className="vto-sum-count">{items.length}</span>
      </h3>
      <ul>
        {shown.map((i) => (
          <li key={i.key}>
            <span className={`vto-sum-dot ${i.badge}`} title={i.note} aria-hidden="true" />
            <span className="vto-sum-title" title={i.title}>
              {i.title}
            </span>
            {i.owner && <span className="vto-sum-owner">{i.owner.split(' ')[0]}</span>}
          </li>
        ))}
      </ul>
      {items.length > PER_UNIT && (
        <button type="button" className="vto-sum-more" onClick={onToggle}>
          {open ? 'Show fewer' : `+${items.length - PER_UNIT} more`}
        </button>
      )}
    </div>
  );
}

/** The sections, in the order the document runs. */
const VTO_SECTIONS: { part: 'Vision' | 'Traction'; id: string; label: string }[] = [
  { part: 'Vision', id: 'vto-values', label: 'Core Values' },
  { part: 'Vision', id: 'vto-focus', label: 'Core Focus' },
  { part: 'Vision', id: 'vto-target', label: '10-Year Target' },
  { part: 'Vision', id: 'vto-marketing', label: 'Marketing Strategy' },
  { part: 'Vision', id: 'vto-3year', label: '3-Year Picture' },
  { part: 'Traction', id: 'vto-1year', label: '1-Year Plan' },
  { part: 'Traction', id: 'vto-rocks', label: 'Quarterly Rocks' },
  { part: 'Traction', id: 'vto-issues', label: 'Issues List' },
];

/**
 * Jump links, grouped the way EOS splits the page.
 *
 * Grouped rather than a flat list because Vision and Traction is the division
 * the whole tool is organised around, and the grouping is the one piece of
 * structure a reader needs before they start.
 */
function VtoContents() {
  return (
    <nav className="vto-contents mb-4" aria-label="V/TO sections">
      {(['Vision', 'Traction'] as const).map((part) => (
        <div className="vto-contents-group" key={part}>
          <span className="eyebrow">{part}</span>
          <div className="vto-contents-links">
            {VTO_SECTIONS.filter((sec) => sec.part === part).map((sec) => (
              <a key={sec.id} href={`#${sec.id}`}>
                {sec.label}
              </a>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

/**
 * The seam between the two halves of the document.
 *
 * EOS prints the V/TO as two columns — vision on the left, traction on the
 * right — and reading it top to bottom loses that, which is what made eight
 * identical cards feel like a form rather than a plan. A labelled band is the
 * vertical equivalent: it says which half you are in and what it is for.
 */
function VtoBand({ part, title, lede }: { part: string; title: string; lede: string }) {
  return (
    <div className="vto-band">
      <span className="vto-band-part">{part}</span>
      <div>
        <h2>{title}</h2>
        <p>{lede}</p>
      </div>
    </div>
  );
}

function PictureCard({
  anchor,
  picture,
  title,
  eyebrow,
  lede,
  onChange,
  onError,
}: {
  picture: Picture;
  title: string;
  eyebrow: string;
  lede: string;
  /** Target for the contents nav. */
  anchor: string;
  onChange: () => void;
  onError: (err: unknown) => void;
}) {
  const patch = (body: Record<string, unknown>) =>
    api.put(`/vto/pictures/${picture.PictureId}`, body).then(onChange).catch(onError);

  return (
    <section className="card" id={anchor}>
      <div className="card-head">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p className="sub">{lede}</p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() =>
            void api
              .post(`/vto/pictures/${picture.PictureId}/items`, {
                text: 'New item',
                sortOrder: picture.items.length * 10,
              })
              .then(onChange)
              .catch(onError)
          }
        >
          Add item
        </button>
      </div>
      <div className="card-body stack">
        <div className="form-grid">
          <div className="field">
            <label>Future date</label>
            <input
              className="input"
              type="date"
              defaultValue={picture.FutureDate?.slice(0, 10) ?? ''}
              onBlur={(e) => void patch({ futureDate: e.target.value || null })}
            />
          </div>
          <div className="field">
            <label>Revenue</label>
            <input
              className="input mono"
              type="number"
              step="1000"
              defaultValue={picture.Revenue ?? ''}
              onBlur={(e) => void patch({ revenue: e.target.value === '' ? null : Number(e.target.value) })}
            />
            <span className="xs subtle">{formatNumber(picture.Revenue, 'currency')}</span>
          </div>
          <div className="field">
            <label>Profit</label>
            <input
              className="input mono"
              type="number"
              step="1000"
              defaultValue={picture.Profit ?? ''}
              onBlur={(e) => void patch({ profit: e.target.value === '' ? null : Number(e.target.value) })}
            />
            <span className="xs subtle">{formatNumber(picture.Profit, 'currency')}</span>
          </div>
        </div>

        <div className="field">
          <label>Measurables</label>
          <InlineEdit
            value={picture.Measurables}
            rows={2}
            placeholder="The other numbers that define this picture"
            onSave={(measurables) => void patch({ measurables })}
          />
        </div>

        <div className="field">
          <label>What it looks like</label>
          {picture.items.length === 0 && <p className="muted small">Nothing listed yet.</p>}
          {picture.items.map((item) => (
            <div key={item.PictureItemId} className="row" style={{ alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                style={{ marginTop: 8, accentColor: 'var(--accent-500)' }}
                checked={item.IsComplete}
                onChange={(e) =>
                  void api
                    .put(`/vto/picture-items/${item.PictureItemId}`, { isComplete: e.target.checked })
                    .then(onChange)
                    .catch(onError)
                }
              />
              <div className="grow">
                <InlineEdit
                  value={item.Text}
                  rows={1}
                  style={item.IsComplete ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
                  onSave={(text) =>
                    void api.put(`/vto/picture-items/${item.PictureItemId}`, { text }).then(onChange).catch(onError)
                  }
                />
              </div>
              <DeleteButton
                what="this item"
                label="✕"
                onConfirm={() =>
                  void api.del(`/vto/picture-items/${item.PictureItemId}`).then(onChange).catch(onError)
                }
              />
            </div>
          ))}
        </div>

        {picture.FutureDate && (
          <p className="xs muted">Target date {formatDate(picture.FutureDate)}</p>
        )}
      </div>
    </section>
  );
}
